/**
 * Display date and time localized to {@link user.lang} in templates.
 *
 * Set up a Stargazer controller for {@link Date} objects.
 *
 * @module
 * @format
 */

/**
 * @import {Controller} from "@acng/frontend-stargazer"
 */

import {COMPILE, UPGRADE} from '@acng/frontend-stargazer';
import {isAttributeNode, isCommentNode, isTextNode} from '@acng/frontend-bounty/dom/type.js';
import {assign} from '@acng/frontend-bounty/object.js';
import {create, TIME} from '@acng/frontend-bounty/dom/element.js';
import {PROTOTYPE} from '@acng/frontend-bounty/std/value.js';
import {now} from '@acng/frontend-bounty/std/date.js';
import {guard, IS} from '@acng/frontend-rubicon';
import {user} from './user/user.js';

const hour = 36e5;
const day = 24 * hour;

const datePrefix = /** @type {const} */ ({
  de: 'am ',
  en: 'on ',
});

const timePrefix = /** @type {const} */ ({
  de: 'um ',
  en: 'at ',
});

const onlyTime = /** @type {const} */ ({timeStyle: 'short'});
const onlyDate = /** @type {const} */ ({dateStyle: 'long'});
const dateAndTime = /** @type {const} */ ({dateStyle: 'full', timeStyle: 'short'});
const fullAndLong = /** @type {const} */ ({dateStyle: 'full', timeStyle: 'long'});

/**
 * @param {Date} date
 * @param {"de" | "en"} lang
 * @returns {string}
 */
const createText = (date, lang) => {
  const dt = now() - date.getTime();

  const options =
    dt < day ? onlyTime
    : dt < 6 * day ? dateAndTime
    : onlyDate;

  const prefix = dt < day ? timePrefix[lang] : datePrefix[lang];

  return prefix + date.toLocaleString(lang, options);
};

/**
 * @param {HTMLTimeElement} element
 * @param {Date} date
 * @returns {void}
 */
const applyToElement = (element, date) => {
  const {lang} = user;
  element.textContent = createText(date, lang);
  element.dateTime = date.toISOString();
  element.title = date.toLocaleString(lang, fullAndLong);
};

/**
 * @param {Element} element
 * @param {Comment | Text | Attr} node
 * @this {Date}
 */
function control(element, node) {
  const date = this;

  if (isTextNode(node)) {
    const element = create(TIME);
    applyToElement(element, date);

    return element;
  }

  if (isAttributeNode(node)) {
    return date.toISOString();
  }

  if (isCommentNode(node)) {
    ASSERT: guard(element, IS(HTMLTimeElement));
    applyToElement(element, date);
  }
}

/**
 * @type {Controller}
 */
const datetimeController = {
  [COMPILE]: control,
  [UPGRADE]: control,
};

assign(Date[PROTOTYPE], datetimeController);
